import { useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { ParsedUrlQuery, stringify } from 'querystring'
import styled, { css } from 'styled-components'
import { z } from 'zod'

import { AlertBanner } from '@awazevr/design-system.components.alert-banner'
import {
  AwazeThemeProvider,
  Brand,
} from '@awazevr/design-system.components.awaze-theme-provider'
import { CountdownBanner } from '@awazevr/design-system.components.countdown-banner'
import { Link } from '@awazevr/design-system.components.link'
import { IFooterLabels } from '@awazevr/design-system.compositions.footer/dist/footer.types'
import { ResponsiveHeader } from '@awazevr/design-system.compositions.responsive-header'
import { useDisclosure } from '@awazevr/design-system.utils.react'
import { PlacesProvider } from '@awazevr/mfe-common.api.places-api'
import {
  GuestAuthProvider,
  Session,
} from '@awazevr/mfe-common.contexts.guest-auth'
import { withServerSideProps } from '@awazevr/mfe-common.contexts.guest-auth/dist/pages/getServerSideProps'
import type {
  IntlProviderProps,
  Languages,
} from '@awazevr/mfe-common.contexts.intl'
import { IntlProvider } from '@awazevr/mfe-common.contexts.intl'
import { SearchContext } from '@awazevr/mfe-common.contexts.search'
import { TmsProvider } from '@awazevr/mfe-common.contexts.tms'
import { parseItems } from '@awazevr/mfe-common.lib.contentful/dist/api/utils/utils_legacy'
import { parseConfig } from '@awazevr/mfe-common.lib.contentful-parse-search-form-config'
import {
  getExperimentVariationKey,
  getOptimizelyUserId as getHeaderValueFromCookiesOrResponse,
  Optimizely,
} from '@awazevr/mfe-common.lib.optimizely'
import { parseForceDecisionsFromUrlQuery } from '@awazevr/mfe-common.lib.optimizely/dist/utils/parseForceDecisionsFromUrlQuery'
import type { SearchCriteria } from '@awazevr/mfe-common.types-and-mocks.site-search-service'
import { useAgencyParamTracker } from '@awazevr/mfe-common.utils.agency-param-tracker'
import {
  createLastMinuteDynamicURL,
  ensureStartDateNotInPast,
} from '@awazevr/mfe-common.utils.search-url'

import { applyLodgesAndParksRename } from '@/__experimental__/AS_2665_Lodges_And_Parks_Rename'
import { serverSideSortOrderParameterForExp } from '@/__experimental__/GD_158_CDC_Sort_Order_Lates/serverSideSortOrderParameterForExp'
import CustomHead from '@/components/CustomHead'
import { Footer } from '@/components/Footer/Footer'
import { SearchFormComponent } from '@/components/SearchForm'
import { mapSearchCriteriaToInitialValues } from '@/components/SearchForm/SearchForm.helper'
import {
  getPropertySuggestionScope,
  isGooglePlacesViaSuggestionsVariant,
} from '@/components/SearchForm/SearchForm.utils'
import { onDesktop, onTablet } from '@/components/utils/dimension'
import { PublicConfig } from '@/config/env'
import {
  CORRELATION_ID_COOKIE_NAME,
  DEFAULT_PAGE_NUMBER,
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
  SORT_OPTIONS_ENUM,
} from '@/constants'
import { AppConfigProvider } from '@/context/app-config-provider'
import { OptimizelyDecisionContextProvider } from '@/context/optimizely-decisions-context'
import { ageCountdownBanner } from '@/experiment/AGE_COUNTDOWN_BANNER'
import {
  clearSearchBetaAccess,
  hasSearchBetaAccess,
  isSearchBetaEligible,
  isSearchBetaEnabled,
  persistSearchBetaAccess,
  SEARCH_EXPERIENCE_COOKIE,
} from '@/experiment/SEARCH_BETA_SPIKE/searchExperiencePreference'
import SearchLayout from '@/layouts/SearchLayout'
import { useMobileInert } from '@/utils/useMobileInert'

const BetaSearchLayout = dynamic(() =>
  import(
    '@/experiment/SEARCH_BETA_SPIKE/BetaSearchLayout/BetaSearchLayout'
  ).then((module) => module.BetaSearchLayout),
)
import { ApplicationConfigProvider } from '@/lib/applicationConfig'
import { DisplayContextProvider } from '@/lib/display/displayContext'
import { parseDisplayMode } from '@/lib/display/displayModeParser'
import { buildStatefromUrlParams } from '@/lib/state/params'
import {
  BookingRange,
  ContentfulDataProps,
  ContentfulHeader,
  OptimizelyData,
} from '@/types'
import {
  contentfulFactory,
  contentfulGlobalFactory,
  getGlobalsFromContentful,
  getLocaleFromContentful,
  getPageFromContentful,
} from '@/utils/contentful/client/contentful-client-helpers'
import { resolvedOrError } from '@/utils/contentful/resolvedOrError'
import { createFullContextQuery } from '@/utils/createFullContextQuery'
import { GlobalStyling } from '@/utils/globalstyle'
import {
  cleanObjectOf,
  createInitialValuesFromConfig,
  getCookie,
  getEndOfDayAsUTCString,
  getHeaderString,
  OldBrand,
  setCookie,
} from '@/utils/helpers'
import { logger } from '@/utils/Logger/'
import { resolveExperimentFlags, setupOptimizely } from '@/utils/optimizely'
import { getOptimizelySdkLogLevel } from '@/utils/optimizely/debugOptimizelyEvents'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'
import { parseAppConfig } from '@/utils/parseConfig'
import { unwrapMenu } from '@/utils/responsiveHeader/responsiveHeaderMap'
import showHeaderPhoneNumber from '@/utils/showHeaderPhoneNumber'
import type { TranslationsDebug } from '@/utils/translations/debug-translations'
import {
  buildTranslationsDebug,
  isTranslationsDebugEnabled,
  logTranslationsDebug,
  TRANSLATIONS_DEBUG_QUERY_PARAM,
} from '@/utils/translations/debug-translations'
import { getTranslations } from '@/utils/translations/get-translations'
import { typeCheckBrand } from '@/utils/typeCheckBrand'

export interface IndexPageProps {
  brand: Brand
  clientHeader?: string
  themeBrand: Brand
  currency: string
  searchCriteria: SearchCriteria
  language: Languages
  contentfulLocale: string
  host: string
  country: string
  contentfulData: ContentfulDataProps
  marketCode: string | null
  optimizelyData?: OptimizelyData
  translations: IntlProviderProps['translations']
  translationsDebug?: TranslationsDebug
  userAgent?: string
  userDeviceType: string
  correlationId?: string
  query: ParsedUrlQuery
  isGuestApp: boolean
  dynamicQuery: ParsedUrlQuery
  showPhoneIcon?: boolean
  session: Session | null
  bookingRangeValue: BookingRange
  experimentFlags: OPTIMIZELY_FLAGS_ENUM[]
  appConfig: PublicConfig
  betaSpikeActive?: boolean
}

const Content = styled.div<{ $flushToHeader: boolean }>`
  ${onTablet} {
    margin-top: ${({ $flushToHeader }) => ($flushToHeader ? 0 : '8px')};
  }
  ${onDesktop} {
    margin-top: ${({ $flushToHeader }) => ($flushToHeader ? 0 : '16px')};
  }
`

// Styles apply only in beta search so the live header keeps its plain wrapper.
const SiteHeaderWrapper = styled.div<{
  $mobileHidden: boolean
  $wide: boolean
}>`
  ${({ $mobileHidden, $wide }) =>
    $wide &&
    css`
      display: grid;
      grid-template-rows: 1fr;
      max-height: 160px;
      // Beta search hides this by sliding it up, not collapsing it: keeping its
      // layout height stops the results pane below from moving while it animates.
      // It must stay fully opaque (no fade) as the pane scrolls underneath it.
      // Nudge with top, not transform: a transform contains the header's fixed
      // mobile menu, widening the page and pushing fixed controls off-screen.
      position: relative;
      z-index: 16;
      top: ${$mobileHidden
        ? 'calc(-1 * var(--site-header-height, 56px))'
        : '0'};
      transition: top 450ms cubic-bezier(0.2, 0.8, 0.2, 1);

      > * {
        min-height: 0;
      }

      ${onDesktop} {
        display: block;
        max-height: none;
        top: 0;

        > * {
          overflow: visible;
        }

        .nav-header {
          padding-inline: 40px;
        }

        .nav-header > div {
          width: 100%;
          max-width: none;
        }

        // The mega menu is a fixed 1200px centred on the header; with the wide
        // header the nav sits at the right edge, so align the menu to it.
        .nav-header nav nav {
          left: auto;
          right: 40px;
          transform: none;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    `}
`

const createTrackingLocale = (language: string, country: string): string =>
  `${language.toLowerCase()}-${country.toUpperCase()}`

export default function IndexPage({
  brand,
  clientHeader = '',
  currency,
  country,
  searchCriteria,
  contentfulData,
  language,
  contentfulLocale,
  host,
  marketCode,
  optimizelyData,
  themeBrand,
  translations,
  translationsDebug,
  userDeviceType,
  correlationId,
  userAgent,
  query,
  isGuestApp,
  dynamicQuery,
  showPhoneIcon,
  session,
  bookingRangeValue,
  experimentFlags,
  appConfig,
  betaSpikeActive = false,
}: Readonly<IndexPageProps>): JSX.Element {
  if (contentfulData.header.searchForm.labels === undefined) {
    typeof newrelic !== 'undefined' &&
      newrelic.noticeError(
        `Contentful data does not contain the search form labels (contentfulData.header.searchForm.labels) for brand: ${brand}`,
        correlationId ? { correlationId } : undefined,
      )

    throw new Error(
      `Contentful data does not contain the search form labels (contentfulData.header.searchForm.labels) for brand: ${brand}`,
    )
  }

  const { footer } = contentfulData
  const optimizelyLocale = createTrackingLocale(language, country)
  const tmsLocale = optimizelyLocale
  const tmsCurrency = currency.toUpperCase()
  const queryParams = useSearchParams()
  const hasTrackedSearchDateTypeEvent = useRef(false)
  const [isMobileSpikeHeaderHidden, setIsMobileSpikeHeaderHidden] =
    useState(false)
  const siteHeaderRef = useRef<HTMLDivElement>(null)
  useMobileInert(siteHeaderRef, isMobileSpikeHeaderHidden)
  useEffect(() => {
    const header = siteHeaderRef.current
    if (!header || typeof ResizeObserver === 'undefined') return

    const publishHeight = () =>
      header.style.setProperty(
        '--site-header-height',
        `${header.offsetHeight}px`,
      )
    publishHeight()
    const observer = new ResizeObserver(publishHeight)
    observer.observe(header)

    return () => observer.disconnect()
  }, [])
  const [isSwitchingSearchExperience, setIsSwitchingSearchExperience] =
    useState(false)
  const initalDisplayMode =
    parseDisplayMode(queryParams?.get('displayMode') ?? 'LIST') ?? 'LIST'

  useAgencyParamTracker()

  useEffect(() => {
    translationsDebug && logTranslationsDebug(translationsDebug)
  }, [])

  useEffect(() => {
    fetch('/api/search/clientpagerender', {})
      .then(() => {
        /** empty */
      })
      .catch(() => {
        /** empty */
      })
  }, [])

  useEffect(() => {
    if (typeof newrelic !== 'undefined') {
      if (optimizelyData) {
        newrelic.addPageAction('Client-PageView-Search', {
          userId: optimizelyData.userId,
        })
      } else {
        newrelic.noticeError(
          "No optimizelyData set - newrelic.addPageAction('Client-PageView-Search') cannot be called",
          {
            ...(correlationId && { correlationId }),
          },
        )
      }

      newrelic.setCustomAttribute('displayMode', initalDisplayMode)
    }
  }, [])

  useEffect(() => {
    globalThis.window['optimizely_decision'] = optimizelyData?.decisions ?? {}

    optimizelyData &&
      Optimizely.getInstance({
        dataFile: optimizelyData.dataFile,
        eventBatchSize: 1,
        additionalConfig: {
          logLevel: getOptimizelySdkLogLevel(),
        },
      })
        .then(async (optimizelyInst) => {
          typeof newrelic !== 'undefined' &&
            newrelic.addPageAction('Optimizely-PageView-Search', {
              userId: optimizelyData.userId,
              ...(correlationId && { correlationId }),
            })

          // Not assigning to a const as we pass the server decisions down to the client and use in context
          // We only call getDecisions here to bucket the user into the experiment client-side
          // We could re-factor this, as we don't need to pass the decisions down from the server
          await optimizelyInst.getDecisions({
            experimentFlags,
            userId: optimizelyData.userId,
            forceDecisions: parseForceDecisionsFromUrlQuery(query),
            userAttributes: {
              application: 'search-mfe',
              brand,
              host,
              isGuestApp,
              locale: optimizelyLocale,
              ...(userAgent && { $opt_user_agent: userAgent, userAgent }),
              userDeviceType,
              sortQueryParam: query.sort?.toString() ?? '',
              initialSearchQueryString: stringify(query),
              accommodationType: query.accommodationType?.toString() ?? '',
              bookingRange: bookingRangeValue,
            },
          })
        })
        .catch((error) => {
          typeof newrelic !== 'undefined' &&
            newrelic.noticeError(
              `Error tracking optimizely age_search_page_view event: ${error}`,
              correlationId ? { correlationId } : undefined,
            )
        })
  }, [optimizelyData])

  const {
    isOpen: filtersIsOpen,
    open: openFilters,
    close: closeFilters,
  } = useDisclosure()

  if (globalThis.window !== undefined) {
    if (getCookie('onesearch_salt') === null) {
      setCookie('onesearch_salt', Date.now(), getEndOfDayAsUTCString())
    }
  }

  const cleanSearchCriteria = cleanObjectOf({
    data: searchCriteria,
    filterOutValues: [null, undefined, ''],
  })

  let searchFormConfig = {
    ...parseConfig(
      {
        ...contentfulData.header.searchForm.config,
      },
      new Date(),
    ),
    showAccommodationField: Boolean(
      contentfulData.header.searchForm.config?.accommodationType,
    ),
  }

  // Since parseconfig from mfe-common and searchform config doesn't align
  // on types, we have to specifically alter the defaultValue of accommodationType
  searchFormConfig = {
    ...searchFormConfig,
    ...(contentfulData.header.searchForm.config?.accommodationTypeDefault && {
      accommodationType: {
        options: searchFormConfig?.accommodationType?.options || [],
        defaultValue:
          cleanSearchCriteria.accommodationType ||
          contentfulData.header.searchForm.config?.accommodationTypeDefault,
      },
    }),
  }

  // AS-2665: Hoseasons tab rename experiment
  searchFormConfig = applyLodgesAndParksRename(searchFormConfig, optimizelyData)

  const cleanedInitialValuesFromConfig = cleanObjectOf({
    data: createInitialValuesFromConfig(searchFormConfig),
    filterOutValues: [null, undefined, ''],
  })

  const HOSHPSortOrder = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.HOS_HP_SORT_ORDER,
  )

  // See ADR 2
  const trueSearchCriteria = {
    ...cleanedInitialValuesFromConfig,
    ...cleanSearchCriteria,
    ...(cleanSearchCriteria.accommodationTypes === undefined && {
      accommodationTypes: [],
    }),
    ...(cleanSearchCriteria.page === undefined && {
      page: DEFAULT_PAGE_NUMBER,
    }),
    ...(cleanSearchCriteria.sort === undefined && {
      sort: SORT_OPTIONS_ENUM.RECOMMENDED,
    }),
    ...(HOSHPSortOrder === 'variant_a' &&
      cleanSearchCriteria.accommodationType == 'holiday-parks' &&
      cleanSearchCriteria.sort === undefined && {
        sort: SORT_OPTIONS_ENUM.PRICE_ASCENDING,
      }),
    ...(marketCode && { salesMarket: marketCode }),
    ...dynamicQuery,
    displayMode: initalDisplayMode,
  }

  useEffect(() => {
    if (!optimizelyData || hasTrackedSearchDateTypeEvent.current) {
      return
    }

    trackClientSideOptimizelyEvent(
      trueSearchCriteria.start
        ? OPTIMIZELY_EVENTS.DATED_SEARCH
        : OPTIMIZELY_EVENTS.UNDATED_SEARCH,
      optimizelyData,
    )

    hasTrackedSearchDateTypeEvent.current = true
  }, [optimizelyData, trueSearchCriteria.start])

  const trueInitialValues = mapSearchCriteriaToInitialValues(trueSearchCriteria)

  const { bannerData, error, isIn2693BannerExp } = ageCountdownBanner({
    brand,
    language,
    optimizelyData,
  })

  const ids = {
    ...(correlationId && { correlationId }),
    ...(optimizelyData && { userId: optimizelyData.userId }),
  }

  if (error && typeof newrelic !== 'undefined') {
    newrelic.noticeError(error, {
      error: z.prettifyError(error),
      ...ids,
    })
    newrelic.addPageAction('Optimizely-Experiment-Setup-Error', {
      error: z.prettifyError(error),
      flagKey: OPTIMIZELY_FLAGS_ENUM.AGE_COUNTDOWN_BANNER,
      ...ids,
    })
  }

  const wishlistIconEnabledExp = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.APB_4017_WISHLIST_ENTRY_POINTS,
  )

  const shouldShowWishlistIcon =
    wishlistIconEnabledExp === 'wishlist_entry_points_visible'

  const googlePlacesViaSuggestions = isGooglePlacesViaSuggestionsVariant(
    getExperimentVariationKey(
      optimizelyData?.decisions ?? {},
      OPTIMIZELY_FLAGS_ENUM.SEARCH_GOOGLE_PLACES_VIA_SUGGESTIONS,
    ),
  )

  const useCombinedCottagesAndNovasolGroups =
    getExperimentVariationKey(
      optimizelyData?.decisions ?? {},
      OPTIMIZELY_FLAGS_ENUM.ONESEARCH_SINGLE_CARDINALITY_BLENDED,
    ) === 'variant_a'
  const propertySuggestionScope = getPropertySuggestionScope(
    brand,
    getExperimentVariationKey(
      optimizelyData?.decisions ?? {},
      OPTIMIZELY_FLAGS_ENUM.ONESEARCH_HOSEASONS_PARKS_LODGES_SUGGESTIONS,
    ),
  )
  const betaSpikeEnabled = !isGuestApp && betaSpikeActive

  // The URL param grants access; the cookie keeps it across navigation. Only
  // the param (re)sets it, so cookie-only visits don't extend its lifetime.
  const enteredBetaViaParam = query?.searchBeta === 'true'
  useEffect(() => {
    if (betaSpikeEnabled && enteredBetaViaParam) persistSearchBetaAccess()
  }, [betaSpikeEnabled, enteredBetaViaParam])

  const exitBetaSearch = (): void => {
    setIsSwitchingSearchExperience(true)
    clearSearchBetaAccess()

    if (typeof newrelic !== 'undefined') {
      newrelic.addPageAction('Search-Experience-Switch', {
        direction: 'beta_to_traditional',
      })
    }

    // Re-entry is only via the searchBeta param, so it must not survive the exit.
    const url = new URL(window.location.href)
    url.searchParams.delete('searchBeta')
    window.location.replace(url)
  }

  // Find the wishlist menu item from Contentful
  const wishListIcon = unwrapMenu(
    contentfulData?.unparsedHeader?.fields?.desktopMenu?.fields?.menuItems ??
      [],
  ).find((item) => item.type === 'wishlist')

  // Combine experiment decision with Contentful configuration
  const displayWishlistIcon =
    shouldShowWishlistIcon && !!wishListIcon && !!wishListIcon.link?.href

  const showAlertBanner =
    Boolean(contentfulData?.alertBanner?.fields?.text?.trim().length) &&
    !isGuestApp
  const useFirstPartyTmsEndpoint =
    process.env.NODE_ENV !== 'development' && !isGuestApp

  return (
    <AppConfigProvider config={appConfig}>
      <OptimizelyDecisionContextProvider optimizelyData={optimizelyData ?? {}}>
        <GuestAuthProvider initialSession={session}>
          <IntlProvider
            currency={currency}
            language={language}
            repo="search-mfe"
            translations={translations}
            country={country}
          >
            <TmsProvider
              id={isGuestApp ? appConfig.guestAppTmsId : appConfig.tmsId}
              useFirstPartyEndpoint={useFirstPartyTmsEndpoint}
              pageLoadDataObject={{
                awaze_device_type: userDeviceType,
                site_environment: appConfig.APP_ENV,
                site_version: process.env.NEXT_PUBLIC_GIT_HASH,
                site_currency: tmsCurrency,
                site_brand: brand,
                site_sub_brand: '',
                site_locale: tmsLocale,
                page_type: 'search result',
                page_section: 'shop',
                page_name: 'search result',
                page_number: `${trueSearchCriteria.page}`,
                page_id: '',
              }}
            >
              <ApplicationConfigProvider
                config={useMemo(
                  () => ({
                    brand,
                    clientHeader,
                    contentfulLocale,
                    currency,
                    themeBrand,
                  }),
                  [brand, clientHeader, contentfulLocale, currency, themeBrand],
                )}
              >
                <AwazeThemeProvider brand={themeBrand}>
                  <DisplayContextProvider
                    props={{ displayMode: initalDisplayMode }}
                  >
                    <GlobalStyling />
                    <CustomHead
                      brand={themeBrand}
                      pageMetaTitle={contentfulData.pageMetaTitle}
                      pageMetaDescription={contentfulData.pageMetaDescription}
                    />

                    <SearchContext
                      searchCriteria={trueSearchCriteria}
                      brand={brand as OldBrand}
                    >
                      {showAlertBanner && (
                        <div style={{ position: 'relative', zIndex: '10' }}>
                          <AlertBanner
                            variant="caution"
                            renderContainer
                            data-testid="alert-banner"
                          >
                            {contentfulData?.alertBanner?.fields?.text}

                            {contentfulData?.alertBanner?.fields?.link && (
                              <div>
                                <Link
                                  variant="text"
                                  sizes={200}
                                  underline={false}
                                  openInNewTab={
                                    contentfulData?.alertBanner?.fields?.link
                                      ?.fields?.openInNew
                                  }
                                  colourScheme="brand-link"
                                  href={
                                    contentfulData?.alertBanner?.fields?.link
                                      ?.fields?.href ?? ''
                                  }
                                >
                                  {
                                    contentfulData?.alertBanner?.fields?.link
                                      ?.fields?.displayText
                                  }
                                </Link>
                              </div>
                            )}
                          </AlertBanner>
                        </div>
                      )}
                      {isIn2693BannerExp && bannerData && (
                        <CountdownBanner
                          {...bannerData}
                          endDate={new Date(bannerData.endDate)}
                        />
                      )}
                      {isGuestApp ? null : (
                        <SiteHeaderWrapper
                          ref={siteHeaderRef}
                          $mobileHidden={isMobileSpikeHeaderHidden}
                          $wide={betaSpikeEnabled}
                          /*  GD-369 Hack - turning off the header shadow for James Villas */
                          id={
                            brand === 'jamesVillas'
                              ? 'james-villas-shadow-hack-header-GD-369'
                              : undefined
                          }
                        >
                          <ResponsiveHeader
                            brand={themeBrand}
                            user={session?.user}
                            wishlist={{
                              displayWishlistIcon,
                              label: wishListIcon?.link?.displayText || '',
                              href: wishListIcon?.link?.href || '',
                            }}
                            menuItems={{
                              mobileMenu: unwrapMenu(
                                contentfulData?.unparsedHeader?.fields
                                  ?.mobileMenu?.fields?.menuItems ?? [],
                              ),
                              desktopMenu: unwrapMenu(
                                contentfulData?.unparsedHeader?.fields
                                  ?.desktopMenu?.fields?.menuItems ?? [],
                              ),
                            }}
                            labels={{
                              menuButtonText:
                                contentfulData.header.menuButtonText ?? '',
                              closeButtonText:
                                contentfulData.header.closeButtonText ?? '',
                            }}
                            locale={language}
                            showMegaMenu
                            phoneNumber={{
                              displayPhoneIcon: showPhoneIcon,
                              label:
                                contentfulData?.unparsedHeader?.fields
                                  ?.desktopMenu?.fields?.menuItems?.[0]?.fields
                                  ?.title ?? '',
                              href:
                                contentfulData?.unparsedHeader?.fields
                                  ?.desktopMenu?.fields?.menuItems?.[0]?.fields
                                  ?.link?.fields?.href ?? '',
                            }}
                          />
                        </SiteHeaderWrapper>
                      )}

                      {/* IMPORTANT Provider scope - See ADR: adr/[2026-01-12]-grapql-providers.md */}
                      {!betaSpikeEnabled && (
                        <PlacesProvider
                          brand={brand}
                          language={contentfulLocale}
                          placesApiUri={appConfig.PLACES_API_URI}
                          suggestionsEndpoint={appConfig.SUGGESTIONS_ENDPOINT}
                          googlePlacesViaSuggestions={
                            googlePlacesViaSuggestions
                          }
                          useCombinedCottagesAndNovasolGroups={
                            useCombinedCottagesAndNovasolGroups
                          }
                          {...(propertySuggestionScope && {
                            propertySuggestionScope,
                          })}
                          headers={{
                            'x-awaze-akamai': appConfig.PLACES_API_AKAMAI_KEY,
                            'x-awaze-client': clientHeader,
                          }}
                        >
                          <SearchFormComponent
                            defaultConfig={searchFormConfig}
                            initialValues={trueInitialValues}
                            openFilters={openFilters}
                            translations={
                              contentfulData.header.searchForm.labels
                            }
                            locale={contentfulLocale}
                            marketCode={marketCode || '826'}
                            isGuestApp={isGuestApp}
                          />
                        </PlacesProvider>
                      )}
                      <Content
                        $flushToHeader={betaSpikeEnabled}
                        className="content-container"
                        id="content-container"
                      >
                        {betaSpikeEnabled ? (
                          <BetaSearchLayout
                            brand={brand}
                            defaultConfig={searchFormConfig}
                            initialValues={trueInitialValues}
                            openFilters={openFilters}
                            searchFormTranslations={
                              contentfulData.header.searchForm.labels
                            }
                            marketCode={marketCode || '826'}
                            isGuestApp={isGuestApp}
                            isSwitchingExperience={isSwitchingSearchExperience}
                            onBackToTraditionalSearch={exitBetaSearch}
                            onMobileHeaderVisibilityChange={
                              setIsMobileSpikeHeaderHidden
                            }
                            experimentDecisions={
                              optimizelyData?.decisions ?? {}
                            }
                            optimizelyUserId={optimizelyData?.userId || ''}
                          />
                        ) : (
                          <SearchLayout
                            brand={brand}
                            filterControls={{
                              isOpen: filtersIsOpen,
                              open: openFilters,
                              close: closeFilters,
                            }}
                            defaultConfig={searchFormConfig}
                            initialValues={trueInitialValues}
                            translations={{
                              searchformTranslations:
                                contentfulData.header.searchForm.labels,
                            }}
                            experimentDecisions={
                              optimizelyData?.decisions ?? {}
                            }
                            optimizelyUserId={optimizelyData?.userId || ''}
                            host={host}
                          />
                        )}
                        {isGuestApp ? null : (
                          <Footer
                            labels={{ ...footer }}
                            brand={themeBrand}
                            currency={currency}
                            country={country}
                          />
                        )}
                      </Content>
                    </SearchContext>
                  </DisplayContextProvider>
                </AwazeThemeProvider>
              </ApplicationConfigProvider>
            </TmsProvider>
          </IntlProvider>
        </GuestAuthProvider>
      </OptimizelyDecisionContextProvider>
    </AppConfigProvider>
  )
}

export const getServerSideProps = withServerSideProps(async (context) => {
  const { headers, cookies } = context.req
  const { session } = context
  const { host = 'NO_HOST', 'user-agent': userAgent = null } = headers

  const brand = getHeaderString(headers, 'x-awaze-brand', process.env.BRAND)
  const country = getHeaderString(
    headers,
    'x-awaze-country',
    process.env.COUNTRY,
  )
  const currency = getHeaderString(
    headers,
    'x-awaze-currency',
    process.env.CURRENCY,
  )
  const language = getHeaderString(
    headers,
    'x-awaze-language',
    process.env.LANGUAGE,
  )
  const marketCode = getHeaderString(
    headers,
    'x-awaze-market-code',
    process.env.MARKET_CODE,
  )
  const isGuestApp =
    getHeaderString(headers, 'x-awaze-client', '') === 'guest-app'
  const clientHeader = getHeaderString(headers, 'x-awaze-client', null)
  const userDeviceType = getHeaderString(headers, 'user-device-type', '')

  const correlationId = getHeaderValueFromCookiesOrResponse({
    cookies,
    response: context.res,
    cookieName: CORRELATION_ID_COOKIE_NAME,
  })

  const appConfig = parseAppConfig(process.env)

  const finalParsedState = ensureStartDateNotInPast(
    buildStatefromUrlParams(context.query),
  )

  // Fixes type error for required typeCheckBrand(brand) call
  if (!brand) {
    logger.error({
      message: `Request did not contain x-awaze-brand header or a default from process.env.BRAND`,
      attributes: {
        statusCode: 500,
      },
      ...(correlationId && {
        correlationId,
      }),
      ...(clientHeader && { 'x-awaze-client': clientHeader }),
    })

    throw new Error(
      `Request did not contain x-awaze-brand header or a default from process.env.BRAND`,
    )
  }

  const themeBrand = getHeaderString(headers, 'x-awaze-theme', brand)

  const requiredHeaders = { brand, country, currency, language }
  const requiredEntries = Object.entries(requiredHeaders)

  for (const [header, headerValue] of requiredEntries) {
    if (!headerValue) {
      logger.error({
        message: `Request did not contain x-awaze-${header} header`,
        attributes: {
          statusCode: 500,
        },
        ...(correlationId && {
          correlationId,
        }),
        ...(clientHeader && { 'x-awaze-client': clientHeader }),
      })

      throw new Error(`Request did not contain x-awaze-${header} header`)
    }
  }

  const allowedBrand = typeCheckBrand(brand)

  if (!allowedBrand) {
    logger.error({
      message: `Request contained an invalid x-awaze-brand header: ${brand}`,
      attributes: {
        statusCode: 500,
      },
      ...(correlationId && {
        correlationId,
      }),
      ...(clientHeader && { 'x-awaze-client': clientHeader }),
    })

    throw new Error(
      `Request contained an invalid x-awaze-brand header: ${brand}`,
    )
  }

  const contentfulClient = await contentfulFactory().getClient()
  if (!contentfulClient) {
    logger.error({
      message: 'NextJs getServerSideProps: Contentful Client not found',
      attributes: {
        statusCode: 500,
      },
      ...(correlationId && {
        correlationId,
      }),
      ...(clientHeader && { 'x-awaze-client': clientHeader }),
    })

    throw new Error(`NextJs getServerSideProps: Contentful Client not found`)
  }

  const requestCountry = country as string
  const requestLanguage = language as string
  const contentfulLocale = await getLocaleFromContentful(
    requestCountry,
    requestLanguage,
    contentfulClient,
  )
  const optimizelyLocale = createTrackingLocale(requestLanguage, requestCountry)

  const globalContentfulClient = await contentfulGlobalFactory().getClient()
  if (!globalContentfulClient) {
    logger.error({
      message:
        'NextJs getServerSideProps: Contentful globalContentfulClient not found',
      attributes: {
        statusCode: 500,
      },
      ...(correlationId && {
        correlationId,
      }),
      ...(clientHeader && { 'x-awaze-client': clientHeader }),
    })

    throw new Error(
      `NextJs getServerSideProps: Contentful globalContentfulClient not found`,
    )
  }

  // Never rejects, so it is safe to leave in flight until the merge below.
  const contentfulTranslationsPromise = getTranslations(
    globalContentfulClient,
    contentfulLocale,
  )

  const [
    { result: contentfulPageData, error: contentfulPageError },
    { result: contentfulGlobalData, error: contentfulGlobalError },
  ] = await Promise.all([
    resolvedOrError(
      getPageFromContentful(
        contentfulClient,
        'searchMFE',
        brand,
        contentfulLocale,
      ),
    ),
    resolvedOrError(
      getGlobalsFromContentful(globalContentfulClient, brand, contentfulLocale),
    ),
  ])

  const contentfulErrorReturn = contentfulPageError || contentfulGlobalError

  if (contentfulErrorReturn) {
    logger.error({
      message:
        'NextJs getServerSideProps: Contentful returned an error fetching data',
      attributes: {
        statusCode: 500,
      },
      ...(correlationId && {
        correlationId,
      }),
      ...(clientHeader && { 'x-awaze-client': clientHeader }),
    })

    throw new Error(
      `NextJs getServerSideProps: Contentful returned an error fetching data`,
    )
  }

  if (contentfulPageData == null || contentfulGlobalData == null) {
    const message = `NextJs getServerSideProps: Contentful Data not found for search page | contentfulPageData is null: ${
      contentfulPageData == null
    } | contentfulGlobalData is null: ${contentfulGlobalData == null}`

    logger.error({
      message,
      attributes: {
        statusCode: 500,
      },
      ...(correlationId && {
        correlationId,
      }),
      ...(clientHeader && { 'x-awaze-client': clientHeader }),
    })

    throw new Error(message)
  }

  const unparsedHeader = contentfulGlobalData.header
  const parsedHeader = parseItems<ContentfulHeader>(contentfulGlobalData.header)
  const parsedFooter = parseItems<IFooterLabels>(contentfulGlobalData.footer)
  const parsedSearchForm = parseItems<ContentfulHeader['searchForm']>(
    contentfulGlobalData.searchForm,
  )

  const combinedHeader = {
    ...(Array.isArray(parsedHeader) ? parsedHeader[0] : parsedHeader),
    searchForm: Array.isArray(parsedSearchForm)
      ? parsedSearchForm[0]
      : parsedSearchForm,
  }

  const formattedContentfulData = {
    name: 'Search Page',
    slug: contentfulPageData.slug,
    pageMetaTitle: contentfulPageData.pageMetaTitle ?? '',
    pageMetaDescription: contentfulPageData.pageMetaDescription ?? '',
    unparsedHeader: unparsedHeader,
    header: combinedHeader,
    footer: Array.isArray(parsedFooter) ? parsedFooter[0] : parsedFooter,
    alertBanner: unparsedHeader?.fields?.alertBanner ?? null,
  }

  const dynamicURL = createLastMinuteDynamicURL(context.query)
  const fullContextQuery = createFullContextQuery(context.query, dynamicURL)

  const bookingRangeValue = serverSideSortOrderParameterForExp(
    fullContextQuery.start,
  )

  // Resolved once here, then reused by the client decide call (passed as a prop).
  const experimentFlags = resolveExperimentFlags({
    start: fullContextQuery.start,
    bookingRange: bookingRangeValue,
  })

  const optimizelyData = await setupOptimizely({
    brand,
    cookies,
    host,
    isGuestApp,
    locale: optimizelyLocale,
    response: context.res,
    sdkKey: process.env.OPTIMIZELY_SDK_KEY,
    query: fullContextQuery,
    userDeviceType,
    ...(userAgent && { userAgent }),
    ...(correlationId && {
      correlationId,
    }),
    bookingRange: bookingRangeValue,
    experimentFlags,
  })

  const { default: translations } = await import(
    `@awazevr/mfe-common.contexts.intl/dist/localization/translation-files/search-mfe/${language}.json`
  )

  const { default: translationsMfeCommon } = await import(
    `@awazevr/mfe-common.contexts.intl/dist/localization/translation-files/mfe-common/${language}.json`
  )

  const contentfulTranslations = await contentfulTranslationsPromise
  const bundledTranslations = { ...translations, ...translationsMfeCommon }

  const showPhoneIcon = showHeaderPhoneNumber(brand, contentfulLocale)

  return {
    props: {
      brand,
      ...(clientHeader && { clientHeader }),
      themeBrand,
      currency,
      country,
      contentfulLocale,
      host,
      language,
      searchCriteria: finalParsedState,
      contentfulData: formattedContentfulData,
      marketCode: marketCode || null,
      // Contentful last, so it wins for every key it holds.
      translations: { ...bundledTranslations, ...contentfulTranslations },
      ...(isTranslationsDebugEnabled(
        context.query[TRANSLATIONS_DEBUG_QUERY_PARAM],
      ) && {
        translationsDebug: buildTranslationsDebug(
          bundledTranslations,
          contentfulTranslations,
        ),
      }),
      userDeviceType,
      ...(optimizelyData && { optimizelyData }),
      ...(userAgent && { userAgent }),
      ...(correlationId && {
        correlationId,
      }),
      query: context.query,
      dynamicQuery: dynamicURL,
      isGuestApp,
      showPhoneIcon,
      session: session ?? null,
      bookingRangeValue,
      experimentFlags,
      appConfig,
      betaSpikeActive:
        isSearchBetaEligible({
          enabled: isSearchBetaEnabled(process.env.SEARCH_BETA_ENABLED),
          brand,
          locale: contentfulLocale,
          brands: process.env.SEARCH_BETA_BRANDS,
        }) &&
        hasSearchBetaAccess(
          context.query.searchBeta,
          cookies[SEARCH_EXPERIENCE_COOKIE],
        ),
    },
  }
})
